import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useDeleteDocument,
  useDocumentDownloadUrl,
  useListDocuments,
  useUploadDocument,
} from "@/hooks/useDocuments";
import {
  getCategoryLabel,
  getCategoryType,
  predefinedCategories,
} from "@/lib/categories";
import { frequencies, getFrequencyLabel } from "@/lib/frequencies";
import {
  cn,
  formatCurrency,
  formatDate,
  formatNumberWithCommas,
  parseNumberWithCommas,
} from "@/lib/utils";
import { AssetService } from "@/services/AssetService";
import TransactionService, {
  type Transaction,
  type CreateTransactionDto,
  type CreateBulkTransactionDto,
} from "@/services/TransactionService";
import type { Asset } from "@/types/asset";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  Calendar,
  CalendarDays,
  CalendarRange,
  Check,
  Download,
  Edit,
  Loader,
  MoreHorizontal,
  PlusCircle,
  Receipt,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import Papa from "papaparse";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { BulkTransactionForm } from "./BulkTransactionForm";
import TransactionDocuments from "./TransactionDocuments";

// Helper function to convert any valid date string to YYYY-MM-DD format
const convertDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString || typeof dateString !== "string") {
    throw new Error("Date string is required and must be a string");
  }

  const trimmedDate = dateString.trim();
  if (!trimmedDate) {
    throw new Error("Date string cannot be empty");
  }

  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
    return trimmedDate;
  }

  // Handle various date formats
  let date: Date;

  try {
    // Try parsing as ISO string first
    if (trimmedDate.includes("T") || trimmedDate.includes("Z")) {
      date = new Date(trimmedDate);
    }
    // Handle MM/DD/YYYY or MM/DD/YY formats
    else if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(trimmedDate)) {
      const parts = trimmedDate.split("/");
      const month = Number.parseInt(parts[0], 10) - 1; // Month is 0-indexed
      const day = Number.parseInt(parts[1], 10);
      const year = Number.parseInt(parts[2], 10);

      // Handle 2-digit years
      const fullYear =
        year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      date = new Date(fullYear, month, day);
    }
    // Handle MM-DD-YYYY or MM-DD-YY formats
    else if (/^\d{1,2}-\d{1,2}-\d{2,4}$/.test(trimmedDate)) {
      const parts = trimmedDate.split("-");
      const month = Number.parseInt(parts[0], 10) - 1;
      const day = Number.parseInt(parts[1], 10);
      const year = Number.parseInt(parts[2], 10);

      const fullYear =
        year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      date = new Date(fullYear, month, day);
    }
    // Handle DD/MM/YYYY or DD/MM/YY formats (European format)
    else if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(trimmedDate)) {
      const parts = trimmedDate.split("/");
      const day = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10) - 1;
      const year = Number.parseInt(parts[2], 10);

      const fullYear =
        year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      date = new Date(fullYear, month, day);
    }
    // Handle DD-MM-YYYY or DD-MM-YY formats (European format)
    else if (/^\d{1,2}-\d{1,2}-\d{2,4}$/.test(trimmedDate)) {
      const parts = trimmedDate.split("-");
      const day = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10) - 1;
      const year = Number.parseInt(parts[2], 10);

      const fullYear =
        year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      date = new Date(fullYear, month, day);
    }
    // Handle YYYY/MM/DD format
    else if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(trimmedDate)) {
      const parts = trimmedDate.split("/");
      const year = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10) - 1;
      const day = Number.parseInt(parts[2], 10);
      date = new Date(year, month, day);
    }
    // Handle MM.DD.YYYY or MM.DD.YY formats
    else if (/^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(trimmedDate)) {
      const parts = trimmedDate.split(".");
      const month = Number.parseInt(parts[0], 10) - 1;
      const day = Number.parseInt(parts[1], 10);
      const year = Number.parseInt(parts[2], 10);

      const fullYear =
        year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      date = new Date(fullYear, month, day);
    }
    // Handle DD.MM.YYYY or DD.MM.YY formats (European format)
    else if (/^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(trimmedDate)) {
      const parts = trimmedDate.split(".");
      const day = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10) - 1;
      const year = Number.parseInt(parts[2], 10);

      const fullYear =
        year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      date = new Date(fullYear, month, day);
    }
    // Handle YYYY.MM.DD format
    else if (/^\d{4}\.\d{1,2}\.\d{1,2}$/.test(trimmedDate)) {
      const parts = trimmedDate.split(".");
      const year = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10) - 1;
      const day = Number.parseInt(parts[2], 10);
      date = new Date(year, month, day);
    }
    // Handle MM DD YYYY format (space-separated)
    else if (/^\d{1,2}\s+\d{1,2}\s+\d{2,4}$/.test(trimmedDate)) {
      const parts = trimmedDate.split(/\s+/);
      const month = Number.parseInt(parts[0], 10) - 1;
      const day = Number.parseInt(parts[1], 10);
      const year = Number.parseInt(parts[2], 10);

      const fullYear =
        year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      date = new Date(fullYear, month, day);
    }
    // Handle DD MM YYYY format (space-separated, European)
    else if (/^\d{1,2}\s+\d{1,2}\s+\d{2,4}$/.test(trimmedDate)) {
      const parts = trimmedDate.split(/\s+/);
      const day = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10) - 1;
      const year = Number.parseInt(parts[2], 10);

      const fullYear =
        year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
      date = new Date(fullYear, month, day);
    }
    // Handle YYYY MM DD format (space-separated)
    else if (/^\d{4}\s+\d{1,2}\s+\d{1,2}$/.test(trimmedDate)) {
      const parts = trimmedDate.split(/\s+/);
      const year = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10) - 1;
      const day = Number.parseInt(parts[2], 10);
      date = new Date(year, month, day);
    }
    // Handle written month formats like "Jan 15, 2025" or "January 15, 2025"
    else if (/^[A-Za-z]+\s+\d{1,2},?\s+\d{2,4}$/.test(trimmedDate)) {
      date = new Date(trimmedDate);
    }
    // Handle written month formats like "15 Jan 2025" or "15 January 2025" (European)
    else if (/^\d{1,2}\s+[A-Za-z]+\s+\d{2,4}$/.test(trimmedDate)) {
      date = new Date(trimmedDate);
    }
    // Fallback: try JavaScript's built-in Date parsing
    else {
      date = new Date(trimmedDate);
    }

    // Validate the parsed date
    if (Number.isNaN(date.getTime())) {
      throw new Error(`Invalid date: ${trimmedDate}`);
    }

    // Check for reasonable date range (1900-2100)
    const year = date.getFullYear();
    if (year < 1900 || year > 2100) {
      throw new Error(
        `Date year ${year} is outside the supported range (1900-2100)`
      );
    }

    // Convert to YYYY-MM-DD format
    const formattedYear = date.getFullYear();
    const formattedMonth = String(date.getMonth() + 1).padStart(2, "0");
    const formattedDay = String(date.getDate()).padStart(2, "0");

    return `${formattedYear}-${formattedMonth}-${formattedDay}`;
  } catch (error) {
    console.error("Error converting date:", trimmedDate, error);
    throw new Error(
      `Invalid date format: "${trimmedDate}". Supported formats: MM/DD/YYYY, MM/DD/YY, DD/MM/YYYY, DD/MM/YY, YYYY-MM-DD, MM-DD-YYYY, DD-MM-YYYY, MM.DD.YYYY, DD.MM.YYYY, YYYY/MM/DD, YYYY.MM.DD, "Jan 15, 2025", "15 Jan 2025", etc.`
    );
  }
};

const transactionSchema = z
  .object({
    name: z.string().optional(),
    category: z.string().min(1, "Category is required"),
    amount: z.number().min(0, "Amount must be positive"),
    date: z.string().min(1, "Date is required"),
    unit: z.number().int().positive().nullable().optional(),
  })
  .refine(
    (data) => {
      // Name is required only for OTHER_INCOME or OTHER_EXPENSE
      if (
        data.category === "OTHER_INCOME" ||
        data.category === "OTHER_EXPENSE"
      ) {
        return !!data.name;
      }
      return true;
    },
    {
      message: "Description is required for Other Income/Expense",
      path: ["name"],
    }
  );

type TransactionFormValues = z.infer<typeof transactionSchema>;

interface AssetTransactionsTabProps {
  assetId: string;
  onAssetDataRefresh?: () => void;
  openAddModal?: boolean;
  initialCategory?: string;
}

interface CSVRowData {
  name: string;
  category: string;
  amount: string;
  date: string;
  transaction_type: string;
  unit?: string;
  [key: string]: string | undefined; // Allow for additional fields
}

interface CSVRow {
  name: string;
  category: string;
  amount: number;
  date: string;
  transaction_type: "INCOME" | "EXPENSE";
  unit?: number | null;
}

// Component to check if transaction has receipts
function ReceiptStatus({ transactionId }: { transactionId: number }) {
  const { data: documents, isLoading } = useListDocuments({
    transaction_id: transactionId,
  });
  const hasReceipts = documents && documents.length > 0;

  return { hasReceipts, isLoading };
}

const importTypeOptions = [
  {
    value: "single",
    label: "Single Transaction",
    icon: <Calendar className="h-4 w-4 mr-1" />,
  },
  {
    value: "ytd",
    label: "Year to Date (YTD)",
    icon: <CalendarDays className="h-4 w-4 mr-1" />,
  },
  {
    value: "month_range",
    label: "Month Range",
    icon: <CalendarRange className="h-4 w-4 mr-1" />,
  },
];

const adaptiveTransactionSchema = z
  .object({
    name: z.string().optional(),
    category: z.string().min(1, "Category is required"),
    amount: z.number().min(0, "Amount must be positive"),
    payee: z.string().optional(),
    transaction_type: z.enum(["INCOME", "EXPENSE"]),
    importType: z.enum(["single", "ytd", "month_range"]),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    frequency: z
      .enum(["monthly", "quarterly", "half_yearly", "annually"])
      .default("monthly"),
    singleDate: z.string().optional(),
  })
  .refine(
    (data) => {
      if (
        data.category === "OTHER_INCOME" ||
        data.category === "OTHER_EXPENSE"
      ) {
        return !!data.name;
      }
      return true;
    },
    {
      message: "Description is required for Other Income/Expense",
      path: ["name"],
    }
  )
  .refine(
    (data) => {
      if (data.importType === "single") {
        return !!data.singleDate;
      }
      return true;
    },
    {
      message: "Date is required for single transaction",
      path: ["singleDate"],
    }
  )
  .refine(
    (data) => {
      if (data.importType === "month_range") {
        return !!data.startDate && !!data.endDate;
      }
      return true;
    },
    {
      message: "Start and end dates are required for month range",
      path: ["startDate"],
    }
  );

type AdaptiveTransactionFormValues = z.infer<typeof adaptiveTransactionSchema>;

// Memoize filtered frequencies outside component for stability
const frequencyOptionsForBulk = frequencies.filter(
  (f) => f.value !== "One-time"
);

// Separate component for the adaptive form to isolate re-renders
interface AdaptiveTransactionFormContentProps {
  assetId: string;
  initialCategory?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function AdaptiveTransactionFormContent({
  assetId,
  initialCategory,
  onSuccess,
  onCancel,
}: AdaptiveTransactionFormContentProps) {
  const [loading, setLoading] = useState(false);
  const [asset, setAsset] = useState<Asset | null>(null);
  const [loadingAsset, setLoadingAsset] = useState(true);
  const defaultDate = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Plain state instead of React Hook Form
  const [formData, setFormData] = useState({
    name: "",
    category: initialCategory || "",
    amount: 0,
    payee: "",
    importType: "single" as const,
    frequency: "monthly" as const,
    singleDate: defaultDate,
    startDate: "",
    endDate: "",
    unit: null as number | null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch asset data to check if it's multi-unit
  useEffect(() => {
    const fetchAsset = async () => {
      try {
        setLoadingAsset(true);
        const assetData = await AssetService.getAsset(assetId);
        console.log("[AdaptiveTransactionFormContent] Fetched asset data:", {
          assetId,
          assetData,
          asset_type: assetData?.asset_type,
          units: assetData?.units,
        });
        setAsset(assetData);
      } catch (error) {
        console.error("Error fetching asset:", error);
      } finally {
        setLoadingAsset(false);
      }
    };
    fetchAsset();
  }, [assetId]);

  // Update a single field
  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Auto-set transaction type based on category
  useEffect(() => {
    if (formData.category) {
      const categoryType = getCategoryType(formData.category);
      // Transaction type is auto-determined, no need to store it
    }
  }, [formData.category]);

  const calculateDateRangeCount = useCallback(
    (startDate: Date, endDate: Date, frequency: string): number => {
      const dates: Date[] = [];
      const current = new Date(startDate);
      while (current <= endDate) {
        dates.push(new Date(current));
        switch (frequency) {
          case "monthly":
            current.setMonth(current.getMonth() + 1);
            break;
          case "quarterly":
            current.setMonth(current.getMonth() + 3);
            break;
          case "half_yearly":
            current.setMonth(current.getMonth() + 6);
            break;
          case "annually":
            current.setFullYear(current.getFullYear() + 1);
            break;
        }
      }
      return dates.length;
    },
    []
  );

  const previewCount = useMemo(() => {
    if (formData.importType === "single") {
      return formData.singleDate ? 1 : 0;
    }
    if (formData.importType === "ytd") {
      const now = new Date();
      const ytdStart = new Date(now.getFullYear(), 0, 1);
      return calculateDateRangeCount(ytdStart, now, formData.frequency);
    }
    if (
      formData.importType === "month_range" &&
      formData.startDate &&
      formData.endDate
    ) {
      return calculateDateRangeCount(
        new Date(formData.startDate),
        new Date(formData.endDate),
        formData.frequency
      );
    }
    return 0;
  }, [formData, calculateDateRangeCount]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    if (
      ["OTHER_INCOME", "OTHER_EXPENSE"].includes(formData.category) &&
      !formData.name
    ) {
      newErrors.name = "Description is required for Other Income/Expense";
    }

    if (formData.amount <= 0) {
      newErrors.amount = "Amount must be positive";
    }

    if (formData.importType === "single" && !formData.singleDate) {
      newErrors.singleDate = "Date is required";
    }

    if (formData.importType === "month_range") {
      if (!formData.startDate) newErrors.startDate = "Start date is required";
      if (!formData.endDate) newErrors.endDate = "End date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const transactionType = getCategoryType(formData.category);

      if (formData.importType === "single") {
        await TransactionService.createTransaction(assetId, {
          name: formData.name || formData.category,
          category: formData.category,
          amount: formData.amount,
          date: formData.singleDate || defaultDate,
          transaction_type: transactionType,
          unit: formData.unit || null,
        });
        toast.success("Transaction added successfully");
      } else {
        const bulkTransaction: CreateBulkTransactionDto = {
          name: formData.name || formData.category,
          category: formData.category,
          amount: formData.amount,
          payee: formData.payee,
          transaction_type: transactionType,
          importType: formData.importType,
          startDate: formData.startDate,
          endDate: formData.endDate,
          frequency: formData.frequency,
          singleDate: formData.singleDate,
          unit: formData.unit || null,
        };
        const response = await TransactionService.createBulkTransactions(
          assetId,
          bulkTransaction
        );
        toast.success(`Successfully created ${response.count} transactions`);
      }
      onSuccess();
    } catch (error) {
      console.error("Error adding transaction:", error);
      toast.error("Failed to add transaction(s)");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SheetHeader className="px-6">
        <SheetTitle>Add Transaction</SheetTitle>
      </SheetHeader>
      <div className="px-6 pb-6">
        <Tabs
          defaultValue="single"
          className="w-full mt-4"
          onValueChange={(value) => updateField("importType", value)}
        >
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="single" className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Single
            </TabsTrigger>
            <TabsTrigger value="ytd" className="flex items-center gap-1">
              <CalendarDays className="h-4 w-4" />
              YTD
            </TabsTrigger>
            <TabsTrigger
              value="month_range"
              className="flex items-center gap-1"
            >
              <CalendarRange className="h-4 w-4" />
              Month Range
            </TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category */}
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => updateField("category", value)}
              >
                <SelectTrigger
                  className={errors.category ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {predefinedCategories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-sm font-medium text-destructive">
                  {errors.category}
                </p>
              )}
            </div>

            {/* Description for OTHER_INCOME/OTHER_EXPENSE */}
            {["OTHER_INCOME", "OTHER_EXPENSE"].includes(formData.category) && (
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="Enter description"
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && (
                  <p className="text-sm font-medium text-destructive">
                    {errors.name}
                  </p>
                )}
              </div>
            )}

            {/* Amount */}
            <div className="space-y-2">
              <Label>Amount</Label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="0.00"
                  className={`pl-8 ${errors.amount ? "border-red-500" : ""}`}
                  value={formatNumberWithCommas(formData.amount)}
                  onChange={(e) => {
                    const value = parseNumberWithCommas(e.target.value);
                    updateField("amount", value);
                  }}
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
              </div>
              {errors.amount && (
                <p className="text-sm font-medium text-destructive">
                  {errors.amount}
                </p>
              )}
            </div>

            {/* Payee */}
            <div className="space-y-2">
              <Label>Payee (Optional)</Label>
              <Input
                placeholder="Enter payee name"
                value={formData.payee}
                onChange={(e) => updateField("payee", e.target.value)}
              />
            </div>

            {/* Unit selector - only show for multi-family properties */}
            {(() => {
              // Debug logging
              console.log("[Add Transaction] Debug unit field visibility:", {
                loadingAsset,
                asset: asset,
                asset_type: asset?.asset_type,
                asset_type_check: asset?.asset_type === "multi_family",
                units: asset?.units,
                units_check: asset?.units && asset.units >= 1,
                full_condition:
                  !loadingAsset &&
                  asset &&
                  asset.asset_type === "multi_family" &&
                  asset.units &&
                  asset.units >= 1,
              });

              const shouldShowUnitField =
                !loadingAsset &&
                asset &&
                asset.asset_type === "multi_family" &&
                asset.units &&
                asset.units >= 1;

              if (!shouldShowUnitField) {
                console.log("[Add Transaction] Unit field hidden. Reasons:", {
                  loadingAsset,
                  noAsset: !asset,
                  notMultiFamily: asset?.asset_type !== "multi_family",
                  noUnits: !asset?.units,
                  unitsLessThan1: asset?.units && asset.units < 1,
                });
              }

              return shouldShowUnitField ? (
                <div className="space-y-2">
                  <Label>Unit (Optional)</Label>
                  <Select
                    value={formData.unit?.toString() || "all"}
                    onValueChange={(value) =>
                      updateField(
                        "unit",
                        value === "all" ? null : Number.parseInt(value, 10)
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Units / Common</SelectItem>
                      {Array.from({ length: asset.units }, (_, i) => i + 1).map(
                        (unitNum) => (
                          <SelectItem key={unitNum} value={unitNum.toString()}>
                            Unit {unitNum}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Leave blank for common expenses or income that applies to
                    all units
                  </p>
                </div>
              ) : null;
            })()}

            {/* Single Date */}
            <TabsContent value="single" className="mt-0">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.singleDate}
                  onChange={(e) => updateField("singleDate", e.target.value)}
                  className={errors.singleDate ? "border-red-500" : ""}
                />
                {errors.singleDate && (
                  <p className="text-sm font-medium text-destructive">
                    {errors.singleDate}
                  </p>
                )}
              </div>
            </TabsContent>

            {/* YTD */}
            <TabsContent value="ytd" className="mt-0">
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Create transactions from January 1st to current date
                </div>
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={formData.frequency}
                    onValueChange={(value) => updateField("frequency", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {frequencyOptionsForBulk.map((freq) => (
                        <SelectItem
                          key={freq.value}
                          value={freq.value.toLowerCase().replace("-", "_")}
                        >
                          {freq.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Month Range */}
            <TabsContent value="month_range" className="mt-0">
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Create transactions for a specific month range
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => updateField("startDate", e.target.value)}
                      className={errors.startDate ? "border-red-500" : ""}
                    />
                    {errors.startDate && (
                      <p className="text-sm font-medium text-destructive">
                        {errors.startDate}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => updateField("endDate", e.target.value)}
                      className={errors.endDate ? "border-red-500" : ""}
                    />
                    {errors.endDate && (
                      <p className="text-sm font-medium text-destructive">
                        {errors.endDate}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={formData.frequency}
                    onValueChange={(value) => updateField("frequency", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {frequencyOptionsForBulk.map((freq) => (
                        <SelectItem
                          key={freq.value}
                          value={freq.value.toLowerCase().replace("-", "_")}
                        >
                          {freq.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Preview */}
            {formData.importType !== "single" && previewCount > 0 && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Preview:</span>
                  <span className="text-xs font-semibold">
                    {previewCount} transactions will be created
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {formData.amount > 0 && (
                    <span>
                      Total amount: $
                      {formatNumberWithCommas(formData.amount * previewCount)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </form>
        </Tabs>
      </div>

      {/* Pinned buttons at bottom */}
      <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-background border-t">
        <div className="flex gap-3">
          <Button
            type="submit"
            className="flex-1"
            disabled={
              loading ||
              (formData.importType !== "single" && previewCount === 0)
            }
            onClick={handleSubmit}
          >
            {loading ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : formData.importType === "single" ? (
              "Add Transaction"
            ) : (
              `Add ${previewCount || 0} Transactions`
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </>
  );
}

export function AssetTransactionsTab({
  assetId,
  onAssetDataRefresh,
  openAddModal,
  initialCategory,
}: AssetTransactionsTabProps) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [asset, setAsset] = useState<Asset | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "date", desc: true },
  ]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [csvData, setCSVData] = useState<CSVRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [categoryMappings, setCategoryMappings] = useState<{
    [key: string]: string;
  }>({});
  const [duplicateCount, setDuplicateCount] = useState(0);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<Transaction | null>(null);

  // Receipt modal state
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedTransactionForReceipt, setSelectedTransactionForReceipt] =
    useState<Transaction | null>(null);
  const receiptFileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadDocument();

  // Check receipt status for selected transaction (always call this hook)
  const {
    data: selectedTransactionDocuments,
    isLoading: isLoadingSelectedReceipts,
  } = useListDocuments({
    transaction_id: selectedTransactionForReceipt?.id || 0,
  });
  const selectedTransactionHasReceipts =
    selectedTransactionDocuments && selectedTransactionDocuments.length > 0;

  const fetchTransactions = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const data = await TransactionService.getAssetTransactions(assetId);
      setTransactions(data);
    } catch (err) {
      console.error("Error fetching transactions:", err);
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  // Fetch asset data to check if it's multi-unit
  useEffect(() => {
    const fetchAsset = async () => {
      if (!assetId) return;
      try {
        const assetData = await AssetService.getAsset(assetId);
        console.log("[AssetTransactionsTab] Fetched asset data:", {
          assetId,
          assetData,
          asset_type: assetData?.asset_type,
          units: assetData?.units,
        });
        setAsset(assetData);
      } catch (error) {
        console.error("Error fetching asset:", error);
      }
    };
    fetchAsset();
  }, [assetId]);

  // Helper function to create a deduplication key from date + amount + category
  const createDedupKey = useCallback(
    (
      date: string,
      amount: number | string,
      category: string,
      unit?: number | null
    ): string => {
      // Normalize date to YYYY-MM-DD format and amount to fixed decimal places
      const normalizedDate = date.split("T")[0]; // Ensure YYYY-MM-DD format
      // Ensure amount is a number before calling toFixed
      const numAmount = typeof amount === "string" ? Number(amount) : amount;
      const normalizedAmount = Number(numAmount.toFixed(2)); // Round to 2 decimal places
      const normalizedCategory = (category || "").trim().toUpperCase(); // Normalize category
      // Include unit in the key if provided (null/undefined means no unit, which is also part of the key)
      const normalizedUnit = unit !== null && unit !== undefined ? unit : "";
      return `${normalizedDate}|${normalizedAmount}|${normalizedCategory}|${normalizedUnit}`;
    },
    []
  );

  // Deduplicate CSV data against existing transactions
  const deduplicateTransactions = useCallback(
    (csvRows: CSVRow[], existingTransactions: Transaction[]): CSVRow[] => {
      // Create a Set of existing transaction keys (date + amount + category + unit)
      const existingKeys = new Set<string>();
      for (const tx of existingTransactions) {
        // Ensure amount is a number
        const txAmount =
          typeof tx.amount === "string" ? Number(tx.amount) : tx.amount;
        const key = createDedupKey(
          tx.date,
          txAmount,
          tx.category || "",
          tx.unit
        );
        existingKeys.add(key);
      }

      // Filter out duplicates from CSV data
      const uniqueRows: CSVRow[] = [];

      for (const row of csvRows) {
        // Ensure amount is a number
        const rowAmount =
          typeof row.amount === "string" ? Number(row.amount) : row.amount;
        const key = createDedupKey(
          row.date,
          rowAmount,
          row.category || "",
          row.unit
        );
        if (!existingKeys.has(key)) {
          uniqueRows.push(row);
          // Add to set to prevent duplicates within CSV itself
          existingKeys.add(key);
        }
      }

      return uniqueRows;
    },
    [createDedupKey]
  );

  // Calculate duplicates when CSV data or transactions change
  useEffect(() => {
    if (
      csvData.length > 0 &&
      transactions.length > 0 &&
      deduplicateTransactions
    ) {
      try {
        const uniqueTransactions = deduplicateTransactions(
          csvData,
          transactions
        );
        const duplicates = csvData.length - uniqueTransactions.length;
        setDuplicateCount(duplicates);
      } catch (error) {
        console.error("Error calculating duplicates:", error);
        setDuplicateCount(0);
      }
    } else {
      setDuplicateCount(0);
    }
  }, [csvData, transactions, deduplicateTransactions]);

  // Add effect to refresh transactions when modal closes
  useEffect(() => {
    if (!showPreview) {
      fetchTransactions();
    }
  }, [showPreview, fetchTransactions]);

  useEffect(() => {
    if (assetId) {
      fetchTransactions();
    }
  }, [assetId, fetchTransactions]);

  const handleDelete = useCallback(
    async (transactionId: number) => {
      try {
        await TransactionService.deleteTransaction(assetId, transactionId);
        toast.success("Transaction deleted successfully");
        fetchTransactions();
        if (onAssetDataRefresh) {
          onAssetDataRefresh();
        }
      } catch (error) {
        console.error("Error deleting transaction:", error);
        toast.error("Failed to delete transaction");
      }
    },
    [assetId, fetchTransactions, onAssetDataRefresh]
  );

  const handleEdit = useCallback((transaction: Transaction) => {
    setEditingTransaction(transaction);
    setEditModalOpen(true);
  }, []);

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditingTransaction(null);
  };

  const handleEditSubmit = async (values: TransactionFormValues) => {
    if (!editingTransaction) return;
    try {
      // Get the transaction type from the category
      const selectedCategory = predefinedCategories.find(
        (cat) => cat.value === values.category
      );
      const transactionType =
        selectedCategory?.type || editingTransaction.transaction_type;

      // Send all required fields to match backend validation schema
      await TransactionService.updateTransaction(
        assetId,
        editingTransaction.id,
        {
          name: values.name || values.category, // Use category as name if not provided
          category: values.category,
          amount: Number(values.amount),
          date: values.date,
          transaction_type: transactionType,
          unit: values.unit ?? null,
        }
      );
      toast.success("Transaction updated successfully");
      setEditModalOpen(false);
      setEditingTransaction(null);
      fetchTransactions();
      if (onAssetDataRefresh) {
        onAssetDataRefresh();
      }
    } catch (err) {
      console.error("Error updating transaction:", err);
      toast.error("Failed to update transaction");
    }
  };

  const handleAddReceipt = useCallback((transaction: Transaction) => {
    setSelectedTransactionForReceipt(transaction);
    setReceiptModalOpen(true);
  }, []);

  const handleReceiptUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTransactionForReceipt) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("transaction_id", String(selectedTransactionForReceipt.id));

    try {
      await uploadMutation.mutateAsync(formData);
      toast.success("Receipt uploaded successfully");
      setReceiptModalOpen(false);
      setSelectedTransactionForReceipt(null);
      if (receiptFileInputRef.current) receiptFileInputRef.current.value = "";
    } catch (error) {
      console.error("Error uploading receipt:", error);
      toast.error("Failed to upload receipt");
    }
  };

  const handleViewReceipts = useCallback((transaction: Transaction) => {
    setSelectedTransactionForReceipt(transaction);
    setReceiptModalOpen(true);
  }, []);

  const handleBulkTransactionSuccess = () => {
    setIsSheetOpen(false);
    fetchTransactions();
    if (onAssetDataRefresh) {
      onAssetDataRefresh();
    }
  };

  // Conditionally include unit column only for multi-family properties
  const columns: ColumnDef<Transaction>[] = useMemo(() => {
    const isMultiFamily =
      asset?.asset_type === "multi_family" && asset?.units && asset.units >= 1;

    const baseColumns: ColumnDef<Transaction>[] = [
      {
        accessorKey: "date",
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Date
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => {
          const date = row.getValue("date") as string;
          return formatDate(date);
        },
      },
      {
        accessorKey: "name",
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Description
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
      },
      {
        accessorKey: "category",
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Category
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => {
          const category = row.getValue("category") as string;
          return getCategoryLabel(category);
        },
      },
    ];

    // Only add unit column for multi-family properties
    if (isMultiFamily) {
      baseColumns.push({
        accessorKey: "unit",
        header: "Unit",
        cell: ({ row }) => {
          const unit = row.getValue("unit") as number | null | undefined;
          if (unit === null || unit === undefined) {
            return <span className="text-muted-foreground text-sm">—</span>;
          }
          return <span className="text-sm font-medium">Unit {unit}</span>;
        },
      });
    }

    baseColumns.push(
      {
        accessorKey: "amount",
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Amount
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => {
          const amount = row.getValue("amount") as number;
          const type = row.original.transaction_type;
          return (
            <div
              className={cn(
                "font-medium",
                type === "INCOME" ? "text-green-600" : "text-red-600"
              )}
            >
              {type === "INCOME" ? "+" : "-"}
              {formatCurrency(Math.abs(amount))}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const transaction = row.original;
          const { hasReceipts, isLoading } = ReceiptStatus({
            transactionId: transaction.id,
          });

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(transaction)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDelete(transaction.id)}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
                {isLoading ? (
                  <DropdownMenuItem disabled>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </DropdownMenuItem>
                ) : hasReceipts ? (
                  <DropdownMenuItem
                    onClick={() => handleViewReceipts(transaction)}
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    View Receipt
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => handleAddReceipt(transaction)}
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    Add Receipt
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      }
    );

    return baseColumns;
  }, [asset, handleEdit, handleDelete, handleAddReceipt, handleViewReceipts]);

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater({
          pageIndex,
          pageSize,
        });
        setPageIndex(newState.pageIndex);
        setPageSize(newState.pageSize);
      } else {
        setPageIndex(updater.pageIndex);
        setPageSize(updater.pageSize);
      }
    },
    state: {
      sorting,
      globalFilter,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    globalFilterFn: (row, columnId, filterValue) => {
      const search = filterValue.toLowerCase();
      const value = row.getValue(columnId);

      if (typeof value === "string") {
        return value.toLowerCase().includes(search);
      }
      if (typeof value === "number") {
        return value.toString().includes(search);
      }
      return false;
    },
  });

  // Memoize default values
  const formDefaultDate = useMemo(
    () => new Date().toISOString().split("T")[0],
    []
  );

  const formDefaultValues = useMemo(
    () => ({
      name: "",
      amount: 0,
      category: "",
      date: formDefaultDate,
      unit: null,
    }),
    [formDefaultDate]
  );

  // Memoize the resolver to prevent recreation on every render
  const formResolver = useMemo(() => zodResolver(transactionSchema), []);

  const form = useForm<TransactionFormValues>({
    resolver: formResolver,
    defaultValues: formDefaultValues,
  });

  // When editingTransaction changes, prefill the form
  useEffect(() => {
    if (editingTransaction) {
      form.reset({
        name: editingTransaction.name || "",
        amount: editingTransaction.amount,
        category: editingTransaction.category,
        date: editingTransaction.date.split("T")[0],
        unit: editingTransaction.unit ?? null,
      });
    }
  }, [editingTransaction, form.reset]);

  // Update handleAddTransaction to use the service
  const handleAddTransaction = async (data: TransactionFormValues) => {
    if (!assetId) return;

    setLoading(true);
    try {
      const selectedCategory = predefinedCategories.find(
        (cat) => cat.value === data.category
      );
      const transactionType = selectedCategory?.type || "EXPENSE";

      const createDto: CreateTransactionDto = {
        name: data.name || data.category, // Use category as name if not provided
        category: data.category,
        amount: Number(data.amount),
        date: data.date,
        transaction_type: transactionType,
        unit: data.unit ?? null,
      };

      await TransactionService.createTransaction(assetId, createDto);
      toast.success("Transaction added successfully");
      setIsSheetOpen(false);
      fetchTransactions();
      if (onAssetDataRefresh) {
        onAssetDataRefresh();
      }
    } catch (err) {
      console.error("Error adding transaction:", err);
      toast.error("Failed to add transaction");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse<CSVRowData>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      transform: (value) => value.trim(),
      complete: (results) => {
        const parsedData: CSVRow[] = [];
        const categoryMappings: { [key: string]: string } = {};

        for (const row of results.data) {
          if (
            row.name &&
            row.category &&
            row.amount &&
            !Number.isNaN(Number(row.amount)) &&
            row.date &&
            row.transaction_type
          ) {
            try {
              // Validate category
              const validCategories = predefinedCategories.map(
                (cat) => cat.value
              );
              const category = row.category.toUpperCase();

              // If category is invalid, map it to OTHER_EXPENSE
              let finalCategory = category;
              if (!validCategories.includes(category)) {
                finalCategory = "OTHER_EXPENSE";
                categoryMappings[row.category] = "OTHER_EXPENSE";
              }

              // Determine transaction type based on category or explicit type
              let transactionType: "INCOME" | "EXPENSE";
              const uppercaseType = row.transaction_type.toUpperCase();

              if (uppercaseType === "INCOME" || uppercaseType === "EXPENSE") {
                transactionType = uppercaseType as "INCOME" | "EXPENSE";
              } else {
                // Fallback to category-based type
                const category = predefinedCategories.find(
                  (cat) => cat.value === finalCategory
                );

                transactionType = category?.type || "EXPENSE";
              }

              // Convert date format
              const convertedDate = convertDateToYYYYMMDD(row.date);

              // Parse unit number if provided
              let unit: number | null | undefined = undefined;
              if (
                row.unit !== undefined &&
                row.unit !== null &&
                row.unit.trim() !== ""
              ) {
                const parsedUnit = Number.parseInt(row.unit.trim(), 10);
                if (!Number.isNaN(parsedUnit) && parsedUnit > 0) {
                  unit = parsedUnit;
                } else if (row.unit.trim() !== "") {
                  // Invalid unit value - log warning but don't fail the row
                  console.warn(
                    `Invalid unit value "${row.unit}" for transaction "${row.name}". Skipping unit.`
                  );
                }
              }

              const transaction: CSVRow = {
                name: row.name,
                category: finalCategory,
                amount: Number(row.amount),
                date: convertedDate,
                transaction_type: transactionType,
                unit: unit,
              };
              parsedData.push(transaction);
            } catch (error) {
              console.error("Error processing row:", row, error);
              toast.error(
                `Error processing row "${row.name}": ${error instanceof Error ? error.message : "Unknown error"}`
              );
            }
          }
        }

        setCSVData(parsedData);
        setShowPreview(true);
        setCategoryMappings(categoryMappings);
        setDuplicateCount(0); // Will be recalculated in useEffect
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        toast.error("Failed to parse CSV file");
      },
    });
  };

  const handleBulkUpload = async () => {
    if (!csvData.length) return;
    if (validationErrors.length > 0) {
      toast.error("Please fix validation errors before uploading");
      return;
    }

    setIsUploading(true);
    try {
      // Fetch existing transactions to check for duplicates
      const existingTransactions =
        await TransactionService.getAssetTransactions(assetId);

      // Deduplicate CSV data against existing transactions
      const uniqueTransactions = deduplicateTransactions(
        csvData,
        existingTransactions
      );

      const duplicateCount = csvData.length - uniqueTransactions.length;

      if (uniqueTransactions.length === 0) {
        toast.warning(
          `All ${csvData.length} transactions are duplicates and were skipped`
        );
        // Reset all state
        setCSVData([]);
        setValidationErrors([]);
        setDuplicateCount(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setShowPreview(false);
        return;
      }

      // Upload unique transactions in batches of 50
      const batchSize = 50;
      for (let i = 0; i < uniqueTransactions.length; i += batchSize) {
        const batch = uniqueTransactions.slice(i, i + batchSize);
        await Promise.all(
          batch.map((row) => {
            const createDto: CreateTransactionDto = {
              name: row.name,
              category: row.category,
              amount: row.amount,
              date: row.date,
              transaction_type: row.transaction_type,
              unit: row.unit,
            };
            return TransactionService.createTransaction(assetId, createDto);
          })
        );
      }

      // Show success message with duplicate info
      if (duplicateCount > 0) {
        toast.success(
          `Successfully uploaded ${uniqueTransactions.length} transactions. ${duplicateCount} duplicate(s) skipped.`
        );
      } else {
        toast.success(
          `Successfully uploaded ${uniqueTransactions.length} transactions`
        );
      }

      // Reset all state
      setCSVData([]);
      setValidationErrors([]);
      setDuplicateCount(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Close modal last
      setShowPreview(false);

      // Refresh asset data to update overview tab
      if (onAssetDataRefresh) {
        onAssetDataRefresh();
      }
    } catch (error) {
      console.error("Error uploading transactions:", error);
      toast.error("Failed to upload transactions");
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create sample data
    const sampleData = [
      {
        name: "Monthly Rent",
        category: "RENT",
        amount: 2000,
        date: "2025-06-28", // Example date in YYYY-MM-DD format
        transaction_type: "INCOME",
        unit: 1, // Example unit number
      },
      {
        name: "Property Insurance",
        category: "INSURANCE",
        amount: 150,
        date: "2025-06-15", // Example date in YYYY-MM-DD format
        transaction_type: "EXPENSE",
        unit: "", // Empty unit for single-unit properties
      },
    ];

    // Use Papa.unparse to generate CSV
    const csvContent = Papa.unparse(sampleData);

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "transaction_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Get duplicate transactions that will be skipped
  const getDuplicateTransactions = useCallback((): CSVRow[] => {
    if (csvData.length === 0 || transactions.length === 0) {
      return [];
    }

    const uniqueTransactions = deduplicateTransactions(csvData, transactions);
    const uniqueKeys = new Set<string>();

    // Create keys for unique transactions
    for (const row of uniqueTransactions) {
      const rowAmount =
        typeof row.amount === "string" ? Number(row.amount) : row.amount;
      const key = createDedupKey(
        row.date,
        rowAmount,
        row.category || "",
        row.unit
      );
      uniqueKeys.add(key);
    }

    // Find duplicates (transactions not in unique set)
    const duplicates: CSVRow[] = [];
    for (const row of csvData) {
      const rowAmount =
        typeof row.amount === "string" ? Number(row.amount) : row.amount;
      const key = createDedupKey(
        row.date,
        rowAmount,
        row.category || "",
        row.unit
      );
      if (!uniqueKeys.has(key)) {
        duplicates.push(row);
      }
    }

    return duplicates;
  }, [csvData, transactions, deduplicateTransactions, createDedupKey]);

  // Create a Set of unique CSV row keys (after deduplication)
  // This is used to efficiently check if a CSV row is a duplicate
  const uniqueCSVRowKeys = useMemo(() => {
    if (csvData.length === 0) {
      return new Set<string>();
    }

    // Get unique CSV rows after deduplication (handles both existing transaction duplicates and CSV-internal duplicates)
    const uniqueTransactions = deduplicateTransactions(csvData, transactions);
    const uniqueKeys = new Set<string>();

    // Create keys for unique CSV rows only
    for (const row of uniqueTransactions) {
      const rowAmount =
        typeof row.amount === "string" ? Number(row.amount) : row.amount;
      const key = createDedupKey(
        row.date,
        rowAmount,
        row.category || "",
        row.unit
      );
      uniqueKeys.add(key);
    }

    return uniqueKeys;
  }, [csvData, transactions, deduplicateTransactions, createDedupKey]);

  // Check if a specific CSV row is a duplicate
  const isDuplicateRow = useCallback(
    (row: CSVRow): boolean => {
      // If no CSV data, nothing can be a duplicate
      if (csvData.length === 0) {
        return false;
      }

      const rowAmount =
        typeof row.amount === "string" ? Number(row.amount) : row.amount;
      const key = createDedupKey(
        row.date,
        rowAmount,
        row.category || "",
        row.unit
      );

      // A row is a duplicate if its key is NOT in the uniqueCSVRowKeys set
      // (meaning it was filtered out by deduplicateTransactions)
      // If uniqueCSVRowKeys is empty but csvData has rows, all rows are duplicates
      return !uniqueCSVRowKeys.has(key);
    },
    [uniqueCSVRowKeys, createDedupKey, csvData.length]
  );

  // Calculate number of unique transactions
  const uniqueTransactionCount = useMemo(() => {
    if (csvData.length === 0) return 0;
    return csvData.length - duplicateCount;
  }, [csvData.length, duplicateCount]);

  const downloadDuplicateTransactions = () => {
    const duplicates = getDuplicateTransactions();

    if (duplicates.length === 0) {
      toast.info("No duplicate transactions to download");
      return;
    }

    // Convert CSVRow to format suitable for CSV export
    const duplicateData = duplicates.map((row) => ({
      name: row.name,
      category: row.category,
      amount: row.amount,
      date: row.date,
      transaction_type: row.transaction_type,
      unit: row.unit ?? "",
    }));

    // Use Papa.unparse to generate CSV
    const csvContent = Papa.unparse(duplicateData);

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const timestamp = new Date().toISOString().split("T")[0];
    link.setAttribute("download", `duplicate_transactions_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success(`Downloaded ${duplicates.length} duplicate transaction(s)`);
  };

  // Reset pagination when transactions change
  useEffect(() => {
    setPageIndex(0);
  }, []);

  // Effect to open add modal if prop is true
  useEffect(() => {
    if (openAddModal && !isSheetOpen) {
      setIsSheetOpen(true);
    }
  }, [openAddModal, isSheetOpen]);

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Recent Transactions</CardTitle>
        <div className="flex items-center gap-2">
          <div className="relative w-60">
            <Input
              placeholder="Search transactions..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8"
            />
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={downloadTemplate}
              title="Download CSV template"
            >
              <Download className="h-4 w-4 mr-1" />
              Template
            </Button>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              ref={fileInputRef}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              title="Import transactions from CSV"
            >
              <Upload className="h-4 w-4 mr-1" />
              Import CSV
            </Button>
            <Sheet
              key={isSheetOpen ? "open" : "closed"}
              open={isSheetOpen}
              onOpenChange={setIsSheetOpen}
            >
              <SheetTrigger asChild>
                <Button size="sm">
                  <PlusCircle className="h-4 w-4 mr-1" />
                  Add Transaction
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[400px] sm:w-[540px] pb-24">
                {isSheetOpen && (
                  <AdaptiveTransactionFormContent
                    assetId={assetId}
                    initialCategory={initialCategory}
                    onSuccess={() => {
                      setIsSheetOpen(false);
                      fetchTransactions();
                      if (onAssetDataRefresh) onAssetDataRefresh();
                    }}
                    onCancel={() => setIsSheetOpen(false)}
                  />
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No transactions found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">
              Showing{" "}
              <strong>
                {table.getState().pagination.pageIndex * pageSize + 1}
              </strong>{" "}
              to{" "}
              <strong>
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * pageSize,
                  table.getFilteredRowModel().rows.length
                )}
              </strong>{" "}
              of <strong>{table.getFilteredRowModel().rows.length}</strong>{" "}
              transactions
            </p>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 30, 40, 50].map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size} rows
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                />
              </PaginationItem>
              {/* Show first page */}
              <PaginationItem>
                <PaginationLink
                  onClick={() => table.setPageIndex(0)}
                  isActive={table.getState().pagination.pageIndex === 0}
                >
                  1
                </PaginationLink>
              </PaginationItem>
              {/* Show ellipsis if needed */}
              {table.getPageCount() > 3 &&
                table.getState().pagination.pageIndex > 1 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}
              {/* Show current page if not first or last */}
              {table.getState().pagination.pageIndex > 0 &&
                table.getState().pagination.pageIndex <
                  table.getPageCount() - 1 && (
                  <PaginationItem>
                    <PaginationLink isActive>
                      {table.getState().pagination.pageIndex + 1}
                    </PaginationLink>
                  </PaginationItem>
                )}
              {/* Show ellipsis if needed */}
              {table.getPageCount() > 3 &&
                table.getState().pagination.pageIndex <
                  table.getPageCount() - 2 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}
              {/* Show last page */}
              {table.getPageCount() > 1 && (
                <PaginationItem>
                  <PaginationLink
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    isActive={
                      table.getState().pagination.pageIndex ===
                      table.getPageCount() - 1
                    }
                  >
                    {table.getPageCount()}
                  </PaginationLink>
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </CardContent>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent
          className="flex flex-col h-[80vh]"
          style={{
            width: "min(98vw, 1800px)",
            maxWidth: "none",
            minWidth: "90vw",
          }}
        >
          <DialogHeader>
            <DialogTitle>Preview Transactions</DialogTitle>
            <DialogDescription>
              Review the transactions before uploading. Make sure all the data
              is correct. Dates are automatically converted to YYYY-MM-DD
              format. Supports various formats including MM/DD/YYYY, DD/MM/YYYY,
              MM-DD-YYYY, written formats like "Jan 15, 2025", and more.
            </DialogDescription>
          </DialogHeader>

          {duplicateCount > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-4">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-medium text-orange-800">
                  Duplicate Detection:
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadDuplicateTransactions}
                  className="h-7 text-xs"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Download Duplicates
                </Button>
              </div>
              <p className="text-sm text-orange-700">
                <strong>{duplicateCount}</strong> transaction(s) will be skipped
                because they already exist (matching date + amount + category).
                Only <strong>{csvData.length - duplicateCount}</strong> unique
                transaction(s) will be uploaded.
              </p>
            </div>
          )}

          {Object.keys(categoryMappings).length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <h4 className="text-sm font-medium text-yellow-800 mb-2">
                Category Mappings:
              </h4>
              <p className="text-sm text-yellow-700 mb-2">
                The following categories were mapped to OTHER_EXPENSE:
              </p>
              <ul className="list-disc list-inside text-sm text-yellow-700">
                {Object.entries(categoryMappings).map(([original, mapped]) => (
                  <li key={original}>
                    {original} → {mapped}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">
              Date Format Information:
            </h4>
            <p className="text-sm text-blue-700 mb-2">
              All dates are automatically converted to YYYY-MM-DD format for the
              database. The preview shows the converted dates.
            </p>
            <p className="text-sm text-blue-700">
              <strong>Supported formats:</strong> MM/DD/YYYY, MM/DD/YY,
              DD/MM/YYYY, DD/MM/YY, MM-DD-YYYY, DD-MM-YYYY, MM.DD.YYYY,
              DD.MM.YYYY, YYYY/MM/DD, YYYY.MM.DD, "Jan 15, 2025", "15 Jan 2025",
              and more.
            </p>
          </div>

          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">Date</TableHead>
                    <TableHead className="w-[400px]">Description</TableHead>
                    <TableHead className="w-[200px]">Category</TableHead>
                    <TableHead className="w-[150px]">Type</TableHead>
                    {(asset?.units && asset.units >= 1) ||
                    csvData.some(
                      (row) => row.unit !== null && row.unit !== undefined
                    ) ? (
                      <TableHead className="w-[100px]">Unit #</TableHead>
                    ) : null}
                    <TableHead className="w-[200px] text-right">
                      Amount
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvData.map((row, index) => {
                    const isDuplicate = isDuplicateRow(row);
                    const showUnitColumn =
                      (asset?.units && asset.units >= 1) ||
                      csvData.some(
                        (r) => r.unit !== null && r.unit !== undefined
                      );
                    return (
                      <TableRow
                        key={index}
                        className={cn(
                          isDuplicate && "bg-orange-50 hover:bg-orange-100"
                        )}
                      >
                        <TableCell>{formatDate(row.date)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {row.name}
                            {isDuplicate && (
                              <span className="text-xs text-orange-600 font-medium">
                                (Duplicate)
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell>{row.transaction_type}</TableCell>
                        {showUnitColumn ? (
                          <TableCell>
                            {row.unit !== null && row.unit !== undefined
                              ? row.unit
                              : "-"}
                          </TableCell>
                        ) : null}
                        <TableCell className="text-right">
                          <div
                            className={cn(
                              "font-medium",
                              row.transaction_type === "INCOME"
                                ? "text-green-600"
                                : "text-red-600",
                              isDuplicate && "opacity-60"
                            )}
                          >
                            {row.transaction_type === "INCOME" ? "+" : "-"}
                            {formatCurrency(Math.abs(row.amount))}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowPreview(false);
                setCSVData([]);
                setDuplicateCount(0);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              Cancel
            </Button>
            {uniqueTransactionCount > 0 && (
              <Button onClick={handleBulkUpload} disabled={isUploading}>
                {isUploading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  `Upload ${uniqueTransactionCount} Transaction${
                    uniqueTransactionCount !== 1 ? "s" : ""
                  }`
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
            <DialogDescription>
              Update the details and save to apply changes.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleEditSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="Description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {predefinedCategories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type="text"
                          step="0.01"
                          min={0}
                          placeholder="0.00"
                          className="pl-8"
                          {...field}
                          value={formatNumberWithCommas(field.value)}
                          onChange={(e) => {
                            const value = parseNumberWithCommas(e.target.value);
                            field.onChange(value);
                          }}
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Unit selector - only show for multi-family properties */}
              {(() => {
                // Debug logging
                const debugInfo = {
                  asset: asset,
                  asset_type: asset?.asset_type,
                  asset_type_check: asset?.asset_type === "multi_family",
                  units: asset?.units,
                  units_check: asset?.units && asset.units >= 1,
                  full_condition:
                    asset?.asset_type === "multi_family" &&
                    asset?.units &&
                    asset.units >= 1,
                };
                console.log(
                  "[Edit Transaction] Debug unit field visibility:",
                  debugInfo
                );

                const shouldShowUnitField =
                  asset?.asset_type === "multi_family" &&
                  asset?.units &&
                  asset.units >= 1;

                if (!shouldShowUnitField) {
                  const reasons = {
                    noAsset: !asset,
                    notMultiFamily: asset?.asset_type !== "multi_family",
                    actualAssetType: asset?.asset_type,
                    noUnits: !asset?.units,
                    unitsValue: asset?.units,
                    unitsLessThan1: asset?.units && asset.units < 1,
                  };
                  console.log(
                    "[Edit Transaction] Unit field hidden. Reasons:",
                    reasons
                  );

                  // Show debug info in development
                  if (process.env.NODE_ENV === "development") {
                    return (
                      <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                        <strong>Debug: Unit field hidden</strong>
                        <pre className="mt-1 text-xs overflow-auto">
                          {JSON.stringify(reasons, null, 2)}
                        </pre>
                      </div>
                    );
                  }
                }

                return shouldShowUnitField ? (
                  <FormField
                    control={form.control}
                    name="unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unit (Optional)</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value?.toString() || "all"}
                            onValueChange={(value) =>
                              field.onChange(
                                value === "all"
                                  ? null
                                  : Number.parseInt(value, 10)
                              )
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select unit (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">
                                All Units / Common
                              </SelectItem>
                              {Array.from(
                                { length: asset.units },
                                (_, i) => i + 1
                              ).map((unitNum) => (
                                <SelectItem
                                  key={unitNum}
                                  value={unitNum.toString()}
                                >
                                  Unit {unitNum}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Leave blank for common expenses or income that applies
                          to all units
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null;
              })()}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleEditModalClose}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={receiptModalOpen} onOpenChange={setReceiptModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedTransactionForReceipt ? "Receipt" : "Add Receipt"}
            </DialogTitle>
            <DialogDescription>
              {selectedTransactionForReceipt
                ? "View receipt for this transaction."
                : "Upload a receipt for this transaction."}
            </DialogDescription>
          </DialogHeader>

          {selectedTransactionForReceipt && (
            <div className="space-y-4">
              {isLoadingSelectedReceipts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="h-6 w-6 animate-spin" />
                </div>
              ) : selectedTransactionHasReceipts ? (
                // Show receipt directly when receipts exist
                <TransactionDocuments
                  transactionId={selectedTransactionForReceipt.id}
                  showDirectReceipt={true}
                />
              ) : (
                // Show upload button when no receipts exist
                <div className="space-y-2">
                  <input
                    type="file"
                    ref={receiptFileInputRef}
                    onChange={handleReceiptUpload}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <Button
                    onClick={() => receiptFileInputRef.current?.click()}
                    className="w-full"
                    disabled={uploadMutation.isPending}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadMutation.isPending
                      ? "Uploading..."
                      : "Upload Receipt"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
